import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { TbPlus, TbTrash, TbEdit } from 'react-icons/tb';
import { generateHTMLFromTemplate } from '../../html-versions/email-template-generator';
import { useAuth } from '../../services/check_login';
import './EmailTemplatesList.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const EmailTemplatesList = () => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      await fetchBrands();
      await fetchTemplates();
    };
    loadData();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/api/email-templates/list');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await api.get('/brand/list');
      setBrands(response.data);
      if (response.data && response.data.length > 0) {
        setSelectedBrand(response.data[0]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching brands:', error);
      setLoading(false);
    }
  };

  const handleDelete = (template) => {
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!templateToDelete) return;
    
    try {
      await api.delete(`/api/email-templates/delete/${templateToDelete._id}`);
      setShowDeleteModal(false);
      setTemplateToDelete(null);
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      setShowDeleteModal(false);
      setTemplateToDelete(null);
    }
  };

  const handleEdit = (template) => {
    navigate('/email-builder', { state: { template } });
  };

  const generatePreviewHTML = (template) => {
    if (!template || !selectedBrand) {
      return '<div style="padding: 40px; text-align: center; color: #999;">No preview available</div>';
    }
    
    const mockCreative = {
      title: template.name || 'Email Template Preview',
      langs: [{
        languageName: 'English',
        headline: 'Sample Headline',
        body: 'This is sample body text for preview',
        bodyBtnName: 'Click Here',
        bodyBtnLink: '#',
        bodyImage: 'https://via.placeholder.com/600x300',
        bodyImageLink: '#',
        additionalText: 'Additional information',
        additionalTextUnderBTN: 'Extra details'
      }]
    };

    try {
      const html = generateHTMLFromTemplate(
        template,
        selectedBrand,
        mockCreative,
        'English'
      );
      
      return html;
    } catch (error) {
      console.error('Error generating preview:', error);
      return '<div style="padding: 40px; text-align: center; color: #f44336;">Error generating preview: ' + error.message + '</div>';
    }
  };

  if (loading) {
    return <div className="templates-loading">Loading templates...</div>;
  }

  return (
    <div className="email-templates-list">
      <div className="templates-header">
        <h1>Email Templates</h1>
        <div className="header-controls">
          <div className="brand-selector">
            <label>Brand Preview:</label>
            <select 
              value={selectedBrand?._id || ''} 
              onChange={(e) => {
                const brand = brands.find(b => b._id === e.target.value);
                setSelectedBrand(brand);
              }}
            >
              {brands.length === 0 ? (
                <option value="">No brands available</option>
              ) : (
                brands.map(brand => (
                  <option key={brand._id} value={brand._id}>{brand.brandName}</option>
                ))
              )}
            </select>
          </div>
          {(user?.role?.permissions?.canCreateEmailTemplates || user?.isAdmin) && (
            <button className="btn-create" onClick={() => navigate('/email-builder')}>
              <TbPlus /> Create New Template
            </button>
          )}
        </div>
      </div>

      {templates.length === 0 ? (
        <div className="no-templates">
          <p>No templates found. Create your first template!</p>
          {(user?.role?.permissions?.canCreateEmailTemplates || user?.isAdmin) && (
            <button className="btn-create-large" onClick={() => navigate('/email-builder')}>
              <TbPlus /> Create Template
            </button>
          )}
        </div>
      ) : (
        <div className="templates-grid">
          {templates.map((template) => (
            <div key={template._id} className="template-card">
              <div className="template-card-header">
                <h3>{template.name}</h3>
                <div className="template-actions">
                  {(user?.role?.permissions?.canEditEmailTemplates || user?.isAdmin) && (
                    <button
                      className="btn-icon btn-icon-modify"
                      onClick={() => handleEdit(template)}
                      title="Edit"
                    >
                      <TbEdit />
                    </button>
                  )}
                  {(user?.role?.permissions?.canDeleteEmailTemplates || user?.isAdmin) && (
                    <button
                      className="btn-icon btn-icon-remove"
                      onClick={() => handleDelete(template)}
                      title="Delete"
                    >
                      <TbTrash />
                    </button>
                  )}
                </div>
              </div>
              
              <div className="template-card-body">
                {selectedBrand ? (
                  <div 
                    className="template-preview-content"
                    dangerouslySetInnerHTML={{
                      __html: generatePreviewHTML(template)
                    }}
                  />
                ) : (
                  <div style={{ 
                    padding: '40px', 
                    textAlign: 'center', 
                    color: '#999',
                    fontFamily: 'Arial, sans-serif'
                  }}>
                    <p>Please select a brand to preview</p>
                  </div>
                )}
              </div>
              
              <div className="template-card-footer">
                <span className="template-date">
                  Created: {new Date(template.createdAt).toLocaleDateString()}
                </span>
                <span className="template-rows">
                  {Array.isArray(template.structure?.blocks)
                    ? `${template.structure.blocks.length} blocks`
                    : `${template.structure?.length || 0} rows`}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowDeleteModal(false);
                  setTemplateToDelete(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete <strong>{templateToDelete?.name}</strong>?
              </p>
              <p className="modal-warning">
                This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-cancel"
                onClick={() => {
                  setShowDeleteModal(false);
                  setTemplateToDelete(null);
                }}
              >
                Cancel
              </button>
              <button
                className="btn-delete-confirm"
                onClick={confirmDelete}
              >
                <TbTrash /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplatesList;
